import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, TextField, InputAdornment,
  Avatar, Button, Popover, Divider, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleIcon from '@mui/icons-material/People';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { fetchScoredLeads, fetchLeadStats } from '../api/conversationApi';
import LogoLoader from '../components/Common/LogoLoader';

const LABEL_COLORS = { hot: '#ef4444', warm: '#f97316', cold: '#3b82f6' };
const LABEL_BG = { hot: '#fef2f2', warm: '#fff7ed', cold: '#eff6ff' };
const LABEL_ICONS = { hot: '🔥', warm: '🟠', cold: '🟡' };

const labelChip = (label) => (
  <Chip
    icon={label === 'hot' ? <WhatshotIcon sx={{ fontSize: 14, color: '#ef4444 !important' }} /> : label === 'warm' ? <WbSunnyIcon sx={{ fontSize: 14, color: '#f97316 !important' }} /> : <AcUnitIcon sx={{ fontSize: 14, color: '#3b82f6 !important' }} />}
    label={label.toUpperCase()}
    size="small"
    sx={{
      fontWeight: 700, fontSize: 10, borderRadius: 1.5, height: 22,
      bgcolor: LABEL_BG[label], color: LABEL_COLORS[label],
      '& .MuiChip-icon': { ml: 0.3 },
    }}
  />
);

const StatCard = ({ icon, label, value, color, bg }) => (
  <Paper elevation={0} sx={{ px: 2.5, py: 2, borderRadius: 3, border: '1px solid #f0f0f0', bgcolor: bg || '#fff', flex: 1, minWidth: 140 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
      <Box sx={{ color: color || '#374151', fontSize: 20, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
    </Box>
    <Typography sx={{ fontSize: 28, fontWeight: 800, color: color || '#111827' }}>{value}</Typography>
  </Paper>
);

const BreakdownPopover = ({ open, anchorEl, onClose, lead }) => (
  <Popover open={open} anchorEl={anchorEl} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} transformOrigin={{ vertical: 'top', horizontal: 'center' }}>
    <Box sx={{ p: 2.5, minWidth: 220, maxWidth: 300 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Score Breakdown</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>
      <Typography sx={{ fontSize: 11, color: '#9ca3af', mb: 1.5 }}>{lead?.name}</Typography>
      {(lead?.breakdown || []).map((b, i) => (
        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.4 }}>
          <Typography sx={{ fontSize: 12, color: '#374151' }}>{b.reason}</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: b.points > 0 ? '#059669' : '#9ca3af' }}>+{b.points}</Typography>
        </Box>
      ))}
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Total</Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: LABEL_COLORS[lead?.label] || '#374151' }}>
          {lead?.score} {LABEL_ICONS[lead?.label]}
        </Typography>
      </Box>
    </Box>
  </Popover>
);

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [breakdownAnchor, setBreakdownAnchor] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const botId = localStorage.getItem('selectedBotId');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchScoredLeads(botId),
      fetchLeadStats(botId),
    ]).then(([leadsRes, statsRes]) => {
      if (leadsRes?.ok) setLeads(leadsRes.leads);
      if (statsRes?.ok) setStats(statsRes.stats);
    }).catch(console.error).finally(() => setLoading(false));
  }, [botId]);

  const filtered = leads.filter(l => {
    if (sourceFilter && l.source !== sourceFilter) return false;
    return l.name.toLowerCase().includes(search.toLowerCase()) ||
      (l.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.phone || '').includes(search);
  });

  const downloadCSV = () => {
    const header = 'Name,Email,Phone,Score,Label,Messages,Date\n';
    const rows = filtered.map(l =>
      `${l.name},${l.email || ''},${l.phone || ''},${l.score},${l.label},${l.msgCount},${new Date(l.date).toLocaleDateString()}`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBreakdown = (e, lead) => {
    setSelectedLead(lead);
    setBreakdownAnchor(e.currentTarget);
  };

  if (loading) return <LogoLoader message="Loading leads..." />;

  const statCards = [
    { icon: <PeopleIcon />, label: 'Total Leads', value: stats?.total || 0, color: '#6366f1', bg: '#f5f3ff' },
    { icon: <WhatshotIcon />, label: 'Hot Leads', value: stats?.hot || 0, color: '#ef4444', bg: '#fef2f2' },
    { icon: <WbSunnyIcon />, label: 'Warm Leads', value: stats?.warm || 0, color: '#f97316', bg: '#fff7ed' },
    { icon: <AcUnitIcon />, label: 'Cold Leads', value: stats?.cold || 0, color: '#3b82f6', bg: '#eff6ff' },
  ];

  const pieData = [
    { name: 'Hot', value: stats?.hot || 0, color: '#ef4444' },
    { name: 'Warm', value: stats?.warm || 0, color: '#f97316' },
    { name: 'Cold', value: stats?.cold || 0, color: '#3b82f6' },
  ].filter(d => d.value > 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Leads</Typography>
          <Typography color="text.secondary" sx={{ fontSize: 13 }}>Scored by engagement: email, phone, appointment, and more</Typography>
        </Box>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadCSV}>
          Export CSV
        </Button>
      </Box>

      {/* Stat Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {statCards.map(s => <StatCard key={s.label} {...s} />)}
      </Box>

      {/* Charts Row */}
      {stats?.dailyTrend?.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Paper elevation={0} sx={{ flex: 2, minWidth: 280, p: 2, borderRadius: 3, border: '1px solid #f0f0f0' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', mb: 1.5 }}>📊 Daily Lead Trend</Typography>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={stats.dailyTrend}>
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="hot" stackId="a" fill="#ef4444" />
                <Bar dataKey="warm" stackId="a" fill="#f97316" />
                <Bar dataKey="cold" stackId="a" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
          {pieData.length > 0 && (
            <Paper elevation={0} sx={{ flex: 1, minWidth: 200, p: 2, borderRadius: 3, border: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', mb: 1 }}>🎯 Lead Distribution</Typography>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={35} outerRadius={65} paddingAngle={3}>
                    {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend formatter={(v) => <span style={{ fontSize: 11 }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          )}
        </Box>
      )}

      {/* Search + Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search by name, email or phone..."
          size="small" sx={{ flex: 1, minWidth: 200 }}
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
        <TextField
          select size="small" sx={{ minWidth: 150 }}
          value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
          SelectProps={{ native: true }}
        >
          <option value="">All Sources</option>
          <option value="bot">Website</option>
          <option value="instagram">Instagram</option>
          <option value="ai_agent">AI Agent</option>
        </TextField>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f7ff' }}>
            <TableRow>
              <TableCell><b>Name</b></TableCell>
              <TableCell><b>Source</b></TableCell>
              <TableCell><b>Contact</b></TableCell>
              <TableCell><b>Score</b></TableCell>
              <TableCell><b>Label</b></TableCell>
              <TableCell><b>Messages</b></TableCell>
              <TableCell><b>Date</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" py={5}>
                    No leads yet. Leads appear here when users interact with your chatbot.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((lead) => (
                <TableRow key={lead._id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: lead.name === 'Anonymous' ? '#9e9e9e' : '#f57c00' }}>
                        {lead.name[0].toUpperCase()}
                      </Avatar>
                      <Typography variant="body2" fontWeight={500}>{lead.name}</Typography>
                      {lead.source === 'instagram' && (
                        <InstagramIcon sx={{ fontSize: 16, color: '#e1306c' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {lead.source === 'instagram' ? (
                      <Chip icon={<InstagramIcon sx={{ fontSize: 12 }} />} label="Instagram" size="small" sx={{
                        bgcolor: '#fce4ec', color: '#e1306c', fontWeight: 600, fontSize: 10,
                      }} />
                    ) : lead.source === 'ai_agent' ? (
                      <Chip label="AI Agent" size="small" sx={{
                        bgcolor: '#f3e8ff', color: '#9333ea', fontWeight: 600, fontSize: 10,
                      }} />
                    ) : (
                      <Chip label="Website" size="small" sx={{
                        bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: 10,
                      }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: lead.email ? '#374151' : '#bbb' }}>{lead.email || '—'}</Typography>
                    <Typography variant="body2" sx={{ color: lead.phone ? '#374151' : '#bbb', fontSize: 11 }}>{lead.phone || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box
                      onClick={(e) => handleBreakdown(e, lead)}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                        color: LABEL_COLORS[lead.label], fontWeight: 700, fontSize: 15,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {lead.score}
                      <InfoOutlinedIcon sx={{ fontSize: 13, opacity: 0.5 }} />
                    </Box>
                  </TableCell>
                  <TableCell>{labelChip(lead.label)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{lead.msgCount}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{new Date(lead.date).toLocaleDateString()}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Breakdown Popover */}
      <BreakdownPopover
        open={Boolean(breakdownAnchor)}
        anchorEl={breakdownAnchor}
        onClose={() => { setBreakdownAnchor(null); setSelectedLead(null); }}
        lead={selectedLead}
      />
    </Box>
  );
};

export default Leads;
